import { AcceptedIntentSchema, RunStateSchema, type RunState, type AcceptedIntent, type Continuity, type SeedEnvelope } from '@dissidium/contracts';
import { add, hash } from './primitives.ts';
import { generateOpening } from '../seed/politics.ts';
import { makePerson } from '../seed/people.ts';
import { registerPerson, killPerson, transferItem } from '../continuity/ledger.ts';

export function createRun(envelope: SeedEnvelope, frozen: Continuity): RunState {
  const opening = generateOpening(envelope, frozen);
  const pcId = `person:pc-${envelope.runOrdinal}`;
  const continuity = registerPerson(opening.continuity, makePerson(pcId, 'The outsider', 'Z03', `run:${envelope.runOrdinal}`));
  return RunStateSchema.parse({ version: 1, envelope, frozenContinuity: structuredClone(frozen), continuity, pcId, political: opening.political, tick: 0, lastSequence: -1, paused: false, participation: 'active', money: 12, consumedEffects: [], events: [], intents: [], intermediateHashes: [] });
}
export function strategicHash(s: RunState): string {
  return hash({ political: s.political, consumedEffects: s.consumedEffects, participation: s.participation });
}
type VersionedRunState = Omit<RunState, 'version'> & { version: number };

export function simulationHash(s: VersionedRunState): string {
  const { intermediateHashes: _hashes, ...state } = s;
  return hash(state);
}
export function reduceIntent(input: RunState, raw: AcceptedIntent): RunState {
  const intent = AcceptedIntentSchema.parse(raw);
  const s = structuredClone(input);
  if (s.participation !== 'active' || s.continuity.persons[s.pcId]?.lifecycle === 'dead') throw new Error('Player participation has ended; no posthumous actions');
  if (intent.tick !== s.tick || intent.sequence !== s.lastSequence + 1) throw new Error('Accepted intentions require the current tick and next sequence');
  const command = intent.command;
  switch (command.type) {
    case 'pause': s.paused = command.paused; break;
    case 'wait': if (!s.paused) s.tick = add(s.tick, command.ticks); break;
    case 'read': case 'rest': break;
    case 'trade': {
      const seller = s.continuity.persons[command.sellerId];
      if (!seller || seller.lifecycle !== 'active' || seller.location !== s.continuity.persons[s.pcId]?.location || command.price !== 3 || s.money < command.price) throw new Error('The posted bench trade requires a living local seller and three cuts');
      s.continuity = transferItem(s.continuity, command.itemId, { kind: 'person', id: seller.id }, { kind: 'person', id: s.pcId });
      s.money = add(s.money, -command.price);
      s.events.push({ type: 'trade', id: `trade:${intent.sequence}`, tick: s.tick, itemId: command.itemId });
      break;
    }
    case 'commit-milestone':
      if (s.consumedEffects.includes(command.milestoneId)) throw new Error('This consequential effect was already consumed');
      // D00's authored contract event. Actual B0–B6 choices arrive in D04.
      s.political.pressure.T02 = Math.min(8, s.political.pressure.T02 + 1);
      s.consumedEffects.push(command.milestoneId);
      s.events.push({ type: 'milestone', id: `milestone:${intent.sequence}`, tick: s.tick, milestoneId: command.milestoneId });
      break;
  }
  s.lastSequence = intent.sequence;
  s.intents.push(intent);
  s.intermediateHashes.push(simulationHash(s));
  return s;
}
export function recordDeath<T extends VersionedRunState>(input: T, personId: string, eventId: string): T {
  const s = structuredClone(input);
  if (s.events.some(e => e.id === eventId)) throw new Error('Death event was already applied');
  const person = s.continuity.persons[personId];
  if (person?.lifecycle === 'dead') {
    if (person.deathEventId !== eventId) throw new Error('Permanent death and its event disagree');
  } else s.continuity = killPerson(s.continuity, personId, eventId);
  s.events.push({ type: 'death', id: eventId, tick: s.tick, personId });
  if (personId === s.pcId) {
    s.participation = 'ended';
    s.consumedEffects.push('authored:abandoned-run');
    s.events.push({ type: 'abandoned-run', id: `abandoned:${eventId}`, tick: s.tick, reason: 'pc-death' });
  }
  s.intermediateHashes.push(simulationHash(s));
  return s;
}
export function replay(envelope: SeedEnvelope, frozen: Continuity, intents: AcceptedIntent[]): RunState {
  return intents.reduce(reduceIntent, createRun(envelope, frozen));
}
